import { Component, Input, Output, EventEmitter, HostListener, ElementRef, OnInit, OnChanges, inject, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { I18nPipe } from '../../pipes/i18n.pipe';
import { I18nService } from '../../../core/services/i18n.service';

export interface MultiSelectItem {
  id: string | number;
  label: string;
}

@Component({
  selector: 'umkt-multi-select-input',
  standalone: true,
  imports: [CommonModule, FormsModule, I18nPipe],
  templateUrl: './multi-select-input.component.html',
  styleUrls: ['./multi-select-input.component.scss'],

})
export class MultiSelectInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() placeholder: string = '';
  @Input() items: MultiSelectItem[] = [];
  @Input() requiredCount: number | null = null;
  @Input() listTitle: string = '';
  @Input() placeholderFilter: string = ''
  @Input() warningMessageTemplate: string = '';
  @Input() selectedItems: MultiSelectItem[] = [];

  private i18nService = inject(I18nService);
  private cdr = inject(ChangeDetectorRef);
  private langSubscription?: Subscription;

  @Output() selectionChange = new EventEmitter<MultiSelectItem[]>();

  isOpen = false;
  _selectedItems: MultiSelectItem[] = [];
  filteredItems: MultiSelectItem[] = [];
  searchTerm: string = '';
  isDisabled = false;
  private elementRef = inject(ElementRef);


  ngOnInit() {
    if (!this.placeholder) {
      this.placeholder = this.i18nService.instant('components.multiSelect.placeholderDefault');
    }
    if (!this.placeholderFilter) {
      this.placeholderFilter = this.i18nService.instant('shared.placeholders.search');
    }

    this.filteredItems = [...this.items];
    this._selectedItems = [...this.selectedItems];

    this.langSubscription = this.i18nService.getCurrentLanguage().subscribe(() => {
      this.cdr.markForCheck(); 
    });
  }

  ngOnChanges() {
    if (!this.selectedItems) {
      return;
    }

    const next = [...this.selectedItems];
    const hasSameSelection =
      next.length === this._selectedItems.length &&
      next.every((item, index) => item.id === this._selectedItems[index]?.id);

    if (!hasSameSelection) {
      this._selectedItems = next;
    }
  }

  ngOnDestroy(): void {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }
  
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }


  toggleDropdown() {
    if (!this.isDisabled) {
      this.isOpen = !this.isOpen;
    }
  }

  selectItem(item: MultiSelectItem) {
    if (this.isSelected(item)) {
      this._selectedItems = this._selectedItems.filter((i) => i.id !== item.id);
    } else {
      if (this.requiredCount === 1 && this._selectedItems.length === 1) {
        this._selectedItems = [item];
      } else
      if (this.requiredCount === null || this._selectedItems.length < this.requiredCount) {
        this._selectedItems.push(item);
      }
    }
    this.searchTerm = '';
    this.filterItems();
    this.propagateChanges();
  }

  removeSelectedItem(itemToRemove: MultiSelectItem, event: Event) {
    event.stopPropagation();
    this._selectedItems = this._selectedItems.filter(item => item.id !== itemToRemove.id);
    this.propagateChanges();
  }

  isSelected(item: MultiSelectItem): boolean {
    return this._selectedItems.some((i) => i.id === item.id);
  }
  
  isSelectionLimitReached(): boolean {
      return this.requiredCount !== null && this._selectedItems.length >= this.requiredCount;
  }

  filterItems() {
    if (!this.searchTerm) {
      this.filteredItems = [...this.items];
    } else {
      const lowerCaseSearch = this.searchTerm.toLowerCase();
      this.filteredItems = this.items.filter(item => 
        item.label.toLowerCase().includes(lowerCaseSearch)
      );
    }
  }

  private propagateChanges() {
    this.selectionChange.emit(this._selectedItems);
  }

  get remainingCount(): number {
    if (this.requiredCount === null) return 0;
    const remaining = this.requiredCount - this._selectedItems.length;
    return remaining > 0 ? remaining : 0;
  }
  
  get warningMessage(): string {
    if (this.requiredCount === null) {
      return '';
    }

    const remaining = this.remainingCount;

    if (remaining <= 0) {
      return '';
    }

    if (this.warningMessageTemplate) {
      return this.warningMessageTemplate.replace('{{count}}', remaining.toString());
    }

    const itemTextKey = remaining === 1 
      ? 'components.multiSelect.itemSingular' 
      : 'components.multiSelect.itemPlural';
    
    const itemText = this.i18nService.instant(itemTextKey);
    
    return this.i18nService.instant('components.multiSelect.warningDefault', { 
      count: remaining, 
      itemText: itemText 
    });
  }
}